import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multiselect";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Calendar,
  Download,
  RefreshCw,
  Search,
  Info,
  Package,
  ShoppingCart,
  Clock,
  Eye,
  FileText,
  Settings,
  Zap,
  Activity,
  Target,
  BarChart3,
  AlertCircle,
  CheckCircle2,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface ForecastDataPoint {
  date: string;
  historicalDemand?: number;
  forecastedDemand?: number;
  upperBound?: number;
  lowerBound?: number;
  reorderPoint?: number;
  currentStock?: number;
}

interface StockItem {
  id: string;
  stockCode: string;
  description: string;
  category: string;
  warehouse: string;
  currentStock: number;
  forecastedDemand: number;
  forecastConfidence: number;
  recommendedAction: "reorder" | "reduce" | "monitor" | "ok";
  reorderQuantity: number;
  estimatedStockoutDate: string | null;
  leadTimeDays: number;
  imageUrl?: string;
  unitCost: number;
  trend: "increasing" | "decreasing" | "stable" | "seasonal";
}

interface ForecastMetrics {
  forecastAccuracy: number;
  accuracyTrend: number;
  stockAtRisk: number;
  overstockItems: number;
  forecastPeriod: string;
}

interface ForecastInsight {
  id: string;
  category: "trending" | "seasonal" | "risk" | "optimization";
  icon: React.ElementType;
  title: string;
  description: string;
  actionLabel: string;
  items?: string[];
}

interface FilterOptions {
  timePeriod: string;
  forecastModel: string;
  categories: string[];
  warehouses: string[];
  confidenceLevel: number;
}

interface ModelMetrics {
  mape: number;
  rmse: number;
  mae: number;
  r2Score: number;
}

// ============================================================================
// MOCK DATA GENERATION
// ============================================================================

const generateHistoricalData = (days: number = 90): ForecastDataPoint[] => {
  const data: ForecastDataPoint[] = [];
  const today = new Date();
  
  for (let i = days; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Generate realistic demand pattern with seasonality and trend
    const baseValue = 100;
    const trend = i * 0.5; // Increasing trend
    const seasonality = Math.sin((i / 7) * Math.PI) * 20; // Weekly pattern
    const noise = (Math.random() - 0.5) * 15;
    
    const historicalDemand = Math.max(0, Math.round(baseValue + trend + seasonality + noise));
    
    data.push({
      date: date.toISOString().split('T')[0],
      historicalDemand,
      reorderPoint: 150,
      currentStock: 180,
    });
  }
  
  return data;
};

const generateForecastData = (days: number = 30): ForecastDataPoint[] => {
  const data: ForecastDataPoint[] = [];
  const today = new Date();
  
  for (let i = 1; i <= days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    
    // Generate forecast with confidence intervals
    const baseValue = 100;
    const trend = (90 + i) * 0.5;
    const seasonality = Math.sin(((90 + i) / 7) * Math.PI) * 20;
    
    const forecastedDemand = Math.max(0, Math.round(baseValue + trend + seasonality));
    const confidenceRange = 25;
    
    data.push({
      date: date.toISOString().split('T')[0],
      forecastedDemand,
      upperBound: forecastedDemand + confidenceRange,
      lowerBound: Math.max(0, forecastedDemand - confidenceRange),
      reorderPoint: 150,
      currentStock: 180,
    });
  }
  
  return data;
};

const generateStockItems = (count: number = 20): StockItem[] => {
  const categories = ["Raw Materials", "Finished Goods", "Components", "Packaging", "Consumables"];
  const warehouses = ["Warehouse A", "Warehouse B", "Warehouse C", "Distribution Center"];
  const trends: Array<"increasing" | "decreasing" | "stable" | "seasonal"> = ["increasing", "decreasing", "stable", "seasonal"];
  const actions: Array<"reorder" | "reduce" | "monitor" | "ok"> = ["reorder", "reduce", "monitor", "ok"];
  
  const items: StockItem[] = [];
  
  for (let i = 0; i < count; i++) {
    const currentStock = Math.floor(Math.random() * 500) + 50;
    const forecastedDemand = Math.floor(Math.random() * 300) + 100;
    const confidence = Math.floor(Math.random() * 25) + 75; // 75-100%
    const leadTime = Math.floor(Math.random() * 28) + 3; // 3-30 days
    
    // Determine action based on stock levels
    let action: "reorder" | "reduce" | "monitor" | "ok";
    let stockoutDate: string | null = null;
    
    if (currentStock < forecastedDemand * 0.5) {
      action = "reorder";
      const daysUntilStockout = Math.floor((currentStock / forecastedDemand) * 30);
      const stockoutDateObj = new Date();
      stockoutDateObj.setDate(stockoutDateObj.getDate() + daysUntilStockout);
      stockoutDate = stockoutDateObj.toISOString().split('T')[0];
    } else if (currentStock > forecastedDemand * 2) {
      action = "reduce";
    } else if (currentStock < forecastedDemand * 0.8) {
      action = "monitor";
    } else {
      action = "ok";
    }
    
    items.push({
      id: `ITEM-${String(i + 1).padStart(4, '0')}`,
      stockCode: `SKU-${String(i + 1).padStart(4, '0')}`,
      description: `Stock Item ${i + 1} - ${categories[i % categories.length]}`,
      category: categories[i % categories.length],
      warehouse: warehouses[i % warehouses.length],
      currentStock,
      forecastedDemand,
      forecastConfidence: confidence,
      recommendedAction: action,
      reorderQuantity: Math.floor(forecastedDemand * 1.5),
      estimatedStockoutDate: stockoutDate,
      leadTimeDays: leadTime,
      unitCost: parseFloat((Math.random() * 100 + 10).toFixed(2)),
      trend: trends[i % trends.length],
    });
  }
  
  return items;
};

const generateInsights = (items: StockItem[]): ForecastInsight[] => {
  const trendingItems = items.filter(i => i.trend === "increasing").slice(0, 3);
  const seasonalItems = items.filter(i => i.trend === "seasonal").slice(0, 3);
  const riskItems = items.filter(i => i.recommendedAction === "reorder").slice(0, 3);
  const overstockItems = items.filter(i => i.recommendedAction === "reduce").slice(0, 2);
  
  return [
    {
      id: "trending",
      category: "trending",
      icon: TrendingUp,
      title: "Trending Items",
      description: `${trendingItems.length} items showing increasing demand patterns. Consider increasing stock levels to meet growing demand.`,
      actionLabel: "View Details",
      items: trendingItems.map(i => i.stockCode),
    },
    {
      id: "seasonal",
      category: "seasonal",
      icon: Activity,
      title: "Seasonal Patterns Detected",
      description: `${seasonalItems.length} items affected by seasonal variations. Adjust forecasts based on historical seasonal trends.`,
      actionLabel: "View Details",
      items: seasonalItems.map(i => i.stockCode),
    },
    {
      id: "risk",
      category: "risk",
      icon: AlertTriangle,
      title: "Critical Stock Risk",
      description: `${riskItems.length} items at risk of stockout within the next 30 days. Immediate action required to prevent disruption.`,
      actionLabel: "Take Action",
      items: riskItems.map(i => i.stockCode),
    },
    {
      id: "optimization",
      category: "optimization",
      icon: Target,
      title: "Optimization Opportunity",
      description: `${overstockItems.length} items with excess inventory. Reduce stock levels to free up capital and warehouse space.`,
      actionLabel: "View Details",
      items: overstockItems.map(i => i.stockCode),
    },
  ];
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DemandForecastingPage() {
  const { isHelpOpen, toggleHelp } = useContextualHelp();
  
  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    timePeriod: "30",
    forecastModel: "ai-ml",
    categories: [],
    warehouses: [],
    confidenceLevel: 90,
  });
  
  // Data states
  const [isLoading, setIsLoading] = useState(false);
  const [stockItems, setStockItems] = useState<StockItem[]>(() => generateStockItems(20));
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  
  // Table states
  const [sortColumn, setSortColumn] = useState<keyof StockItem>("forecastConfidence");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Chart data
  const historicalData = useMemo(() => generateHistoricalData(90), []);
  const forecastData = useMemo(() => generateForecastData(parseInt(filters.timePeriod)), [filters.timePeriod]);
  const combinedChartData = useMemo(() => [...historicalData, ...forecastData], [historicalData, forecastData]);
  
  // Calculate metrics
  const metrics: ForecastMetrics = useMemo(() => {
    const atRisk = stockItems.filter(i => i.recommendedAction === "reorder").length;
    const overstock = stockItems.filter(i => i.recommendedAction === "reduce").length;
    
    return {
      forecastAccuracy: 94.2,
      accuracyTrend: 2.1,
      stockAtRisk: atRisk,
      overstockItems: overstock,
      forecastPeriod: `Next ${filters.timePeriod} days`,
    };
  }, [stockItems, filters.timePeriod]);
  
  // Model performance metrics
  const modelMetrics: ModelMetrics = useMemo(() => ({
    mape: 5.8,
    rmse: 12.3,
    mae: 8.7,
    r2Score: 0.92,
  }), []);
  
  // Generate insights
  const insights = useMemo(() => generateInsights(stockItems), [stockItems]);
  
  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = stockItems;
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.stockCode.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter(item => filters.categories.includes(item.category));
    }
    
    // Warehouse filter
    if (filters.warehouses.length > 0) {
      filtered = filtered.filter(item => filters.warehouses.includes(item.warehouse));
    }
    
    // Confidence level filter
    filtered = filtered.filter(item => item.forecastConfidence >= filters.confidenceLevel);
    
    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      const modifier = sortDirection === "asc" ? 1 : -1;
      
      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * modifier;
      }
      return String(aVal).localeCompare(String(bVal)) * modifier;
    });
    
    return filtered;
  }, [stockItems, searchQuery, filters, sortColumn, sortDirection]);
  
  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Event handlers
  const handleGenerateForecast = useCallback(() => {
    setIsLoading(true);
    console.log("Generating forecast with filters:", filters);
    
    setTimeout(() => {
      const newItems = generateStockItems(20);
      setStockItems(newItems);
      setIsLoading(false);
      toast.success("Forecast Generated", {
        description: `Updated forecast for ${newItems.length} items using ${filters.forecastModel} model.`,
      });
    }, 2000);
  }, [filters]);
  
  const handleExportReport = useCallback(() => {
    console.log("Exporting report for items:", filteredItems.length);
    toast.success("Export Started", {
      description: "Generating CSV report with forecast data...",
    });
  }, [filteredItems]);
  
  const handleScheduleAutoForecast = useCallback(() => {
    console.log("Opening schedule dialog");
    toast.info("Schedule Auto-Forecast", {
      description: "This feature will be available soon.",
    });
  }, []);
  
  const handleSort = useCallback((column: keyof StockItem) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }, [sortColumn]);
  
  const handleSelectAll = useCallback(() => {
    if (selectedItems.size === paginatedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(paginatedItems.map(item => item.id)));
    }
  }, [selectedItems, paginatedItems]);
  
  const handleSelectItem = useCallback((id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  }, [selectedItems]);
  
  const handleCreatePO = useCallback((itemId: string) => {
    console.log("Creating PO for item:", itemId);
    toast.success("Purchase Order", {
      description: "Creating purchase order...",
    });
  }, []);
  
  const handleViewDetails = useCallback((itemId: string) => {
    console.log("Viewing details for item:", itemId);
    toast.info("Item Details", {
      description: "Opening detailed forecast view...",
    });
  }, []);
  
  const handleAdjustForecast = useCallback((itemId: string) => {
    console.log("Adjusting forecast for item:", itemId);
    toast.info("Adjust Forecast", {
      description: "Opening forecast adjustment dialog...",
    });
  }, []);
  
  // Helper functions
  const getActionBadge = (action: string) => {
    switch (action) {
      case "reorder":
        return <Badge variant="destructive">Reorder</Badge>;
      case "reduce":
        return <Badge variant="warning">Reduce Stock</Badge>;
      case "monitor":
        return <Badge variant="info">Monitor</Badge>;
      case "ok":
        return <Badge variant="success">OK</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };
  
  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 90) {
      return <Badge variant="success">{confidence}%</Badge>;
    } else if (confidence >= 80) {
      return <Badge variant="warning">{confidence}%</Badge>;
    } else {
      return <Badge variant="destructive">{confidence}%</Badge>;
    }
  };
  
  const getInsightIcon = (category: string) => {
    const iconMap: Record<string, React.ElementType> = {
      trending: TrendingUp,
      seasonal: Activity,
      risk: AlertTriangle,
      optimization: Target,
    };
    return iconMap[category] || Info;
  };
  
  const getInsightColor = (category: string) => {
    const colorMap: Record<string, string> = {
      trending: "text-blue-600 bg-blue-50 border-blue-200",
      seasonal: "text-purple-600 bg-purple-50 border-purple-200",
      risk: "text-red-600 bg-red-50 border-red-200",
      optimization: "text-green-600 bg-green-50 border-green-200",
    };
    return colorMap[category] || "text-gray-600 bg-gray-50 border-gray-200";
  };
  
  // Help content
  const helpContent = {
    title: "Demand Forecasting",
    sections: [
      {
        title: "Overview",
        content: "AI-powered demand forecasting helps you predict future inventory needs, optimize stock levels, and reduce stockouts. The system analyzes historical data, seasonal patterns, and trends to generate accurate forecasts.",
      },
      {
        title: "Forecast Models",
        items: [
          "AI/ML Model: Advanced machine learning algorithms for highest accuracy",
          "Moving Average: Simple average of recent demand periods",
          "Exponential Smoothing: Weighted average giving more importance to recent data",
          "Seasonal: Accounts for recurring seasonal patterns in demand",
        ],
      },
      {
        title: "Confidence Levels",
        content: "Higher confidence levels (95-99%) provide wider prediction intervals but greater certainty. Lower levels (80-90%) give tighter ranges but with less certainty. Choose based on your risk tolerance.",
      },
      {
        title: "Recommended Actions",
        items: [
          "Reorder: Stock is predicted to fall below reorder point",
          "Reduce Stock: Excess inventory detected, consider reducing orders",
          "Monitor: Stock levels acceptable but watch closely",
          "OK: Stock levels are optimal for forecasted demand",
        ],
      },
    ],
  };
  
  // Category and warehouse options
  const categoryOptions = [
    { value: "Raw Materials", label: "Raw Materials" },
    { value: "Finished Goods", label: "Finished Goods" },
    { value: "Components", label: "Components" },
    { value: "Packaging", label: "Packaging" },
    { value: "Consumables", label: "Consumables" },
  ];
  
  const warehouseOptions = [
    { value: "Warehouse A", label: "Warehouse A" },
    { value: "Warehouse B", label: "Warehouse B" },
    { value: "Warehouse C", label: "Warehouse C" },
    { value: "Distribution Center", label: "Distribution Center" },
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Demand Forecasting</h1>
          <p className="text-muted-foreground mt-2">
            AI-powered demand forecasting to optimize inventory levels and reduce stockouts
          </p>
        </div>
        <HelpButton onClick={toggleHelp} />
      </div>
      
      {/* Summary Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StaticMetricCardAlt
          title="Forecast Accuracy"
          value={`${metrics.forecastAccuracy}%`}
          icon={BarChart3}
          variant="success"
          change={`+${metrics.accuracyTrend}%`}
          changeLabel="from last period"
        />
        <StaticMetricCardAlt
          title="Stock at Risk"
          value={metrics.stockAtRisk.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="items predicted to stock out"
        />
        <StaticMetricCardAlt
          title="Overstock Items"
          value={metrics.overstockItems.toString()}
          icon={Package}
          variant="warning"
          changeLabel="items with excess inventory"
        />
        <StaticMetricCardAlt
          title="Forecast Period"
          value={`${filters.timePeriod} days`}
          icon={Calendar}
          variant="info"
          changeLabel={metrics.forecastPeriod}
        />
      </div>
      
      {/* Filters & Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Forecast Configuration</CardTitle>
          <CardDescription>
            Configure forecast parameters and generate predictions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Time Period */}
            <div className="space-y-2">
              <Label htmlFor="time-period">Time Period</Label>
              <Select
                value={filters.timePeriod}
                onValueChange={(value) => setFilters(prev => ({ ...prev, timePeriod: value }))}
              >
                <SelectTrigger id="time-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Next 7 days</SelectItem>
                  <SelectItem value="14">Next 14 days</SelectItem>
                  <SelectItem value="30">Next 30 days</SelectItem>
                  <SelectItem value="60">Next 60 days</SelectItem>
                  <SelectItem value="90">Next 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Forecast Model */}
            <div className="space-y-2">
              <Label htmlFor="forecast-model">Forecast Model</Label>
              <Select
                value={filters.forecastModel}
                onValueChange={(value) => setFilters(prev => ({ ...prev, forecastModel: value }))}
              >
                <SelectTrigger id="forecast-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ai-ml">AI/ML Model</SelectItem>
                  <SelectItem value="moving-average">Moving Average</SelectItem>
                  <SelectItem value="exponential-smoothing">Exponential Smoothing</SelectItem>
                  <SelectItem value="seasonal">Seasonal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Stock Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Stock Category</Label>
              <MultiSelect
                options={categoryOptions}
                placeholder="All categories"
                value={filters.categories}
                onValueChange={(value) => setFilters(prev => ({ ...prev, categories: value }))}
              />
            </div>
            
            {/* Warehouse */}
            <div className="space-y-2">
              <Label htmlFor="warehouse">Warehouse</Label>
              <MultiSelect
                options={warehouseOptions}
                placeholder="All warehouses"
                value={filters.warehouses}
                onValueChange={(value) => setFilters(prev => ({ ...prev, warehouses: value }))}
              />
            </div>
            
            {/* Confidence Level */}
            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="confidence-level">Confidence Level</Label>
                <span className="text-sm font-medium text-muted-foreground">{filters.confidenceLevel}%</span>
              </div>
              <Slider
                id="confidence-level"
                min={80}
                max={99}
                step={1}
                value={[filters.confidenceLevel]}
                onValueChange={(value) => setFilters(prev => ({ ...prev, confidenceLevel: value[0] }))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>80%</span>
                <span>90%</span>
                <span>95%</span>
                <span>99%</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button onClick={handleGenerateForecast} disabled={isLoading}>
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Generate Forecast
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleExportReport}>
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
            <Button variant="outline" onClick={handleScheduleAutoForecast}>
              <Clock className="h-4 w-4 mr-2" />
              Schedule Auto-Forecast
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Demand Forecast Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Demand Forecast Chart</CardTitle>
          <CardDescription>
            Historical demand (past 90 days) and forecasted demand with confidence intervals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={combinedChartData}>
              <defs>
                <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              />
              <Legend />
              
              {/* Confidence Interval */}
              <Area
                type="monotone"
                dataKey="upperBound"
                stroke="none"
                fill="url(#colorConfidence)"
                name="Confidence Interval"
              />
              <Area
                type="monotone"
                dataKey="lowerBound"
                stroke="none"
                fill="url(#colorConfidence)"
              />
              
              {/* Historical Demand */}
              <Line
                type="monotone"
                dataKey="historicalDemand"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                name="Historical Demand"
              />
              
              {/* Forecasted Demand */}
              <Line
                type="monotone"
                dataKey="forecastedDemand"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Forecasted Demand"
              />
              
              {/* Reorder Point */}
              <ReferenceLine
                y={150}
                stroke="#ef4444"
                strokeDasharray="3 3"
                label={{ value: "Reorder Point", position: "insideTopRight", fill: "#ef4444" }}
              />
              
              {/* Current Stock */}
              <ReferenceLine
                y={180}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: "Current Stock", position: "insideTopRight", fill: "#f59e0b" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      {/* Forecast Data Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Forecast Data</CardTitle>
              <CardDescription>
                Showing {paginatedItems.length} of {filteredItems.length} items
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedItems.size === paginatedItems.length && paginatedItems.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("stockCode")}
                      className="h-8 px-2"
                    >
                      Stock Item
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("currentStock")}
                      className="h-8 px-2"
                    >
                      Current Stock
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("forecastedDemand")}
                      className="h-8 px-2"
                    >
                      Forecasted Demand
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("forecastConfidence")}
                      className="h-8 px-2"
                    >
                      Confidence
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead>Recommended Action</TableHead>
                  <TableHead className="text-right">Reorder Qty</TableHead>
                  <TableHead>Est. Stockout Date</TableHead>
                  <TableHead className="text-right">Lead Time</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      <Package className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                      <p className="font-medium">No items found</p>
                      <p className="text-sm mt-1">Try adjusting your filters or search query</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedItems.has(item.id)}
                          onCheckedChange={() => handleSelectItem(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <div className="font-medium">{item.stockCode}</div>
                            <div className="text-sm text-muted-foreground truncate max-w-xs">
                              {item.description}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{item.currentStock}</TableCell>
                      <TableCell className="text-right font-medium">{item.forecastedDemand}</TableCell>
                      <TableCell>{getConfidenceBadge(item.forecastConfidence)}</TableCell>
                      <TableCell>{getActionBadge(item.recommendedAction)}</TableCell>
                      <TableCell className="text-right">{item.reorderQuantity}</TableCell>
                      <TableCell>
                        {item.estimatedStockoutDate ? (
                          <div className="flex items-center gap-1 text-red-600">
                            <AlertCircle className="h-3 w-3" />
                            <span className="text-sm">{item.estimatedStockoutDate}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{item.leadTimeDays} days</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleViewDetails(item.id)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Details</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleCreatePO(item.id)}
                                >
                                  <ShoppingCart className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Create PO</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleAdjustForecast(item.id)}
                                >
                                  <Settings className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Adjust Forecast</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="items-per-page" className="text-sm">Items per page:</Label>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger id="items-per-page" className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Forecast Insights Panel */}
      <div className="grid gap-4 md:grid-cols-2">
        {insights.map((insight) => {
          const Icon = insight.icon;
          return (
            <Card key={insight.id} className={`border-2 ${getInsightColor(insight.category)}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getInsightColor(insight.category)}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{insight.title}</CardTitle>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{insight.description}</p>
                {insight.items && insight.items.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {insight.items.map((item) => (
                      <Badge key={item} variant="outline" className="text-xs">
                        {item}
                      </Badge>
                    ))}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => toast.info(insight.title, { description: "Opening detailed view..." })}
                >
                  {insight.actionLabel}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Forecast Accuracy Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Model Performance Metrics</CardTitle>
          <CardDescription>
            Statistical measures of forecast accuracy and model performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">MAPE</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">
                        Mean Absolute Percentage Error - measures the average magnitude of errors in percentage terms. Lower is better.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-2xl font-bold">{modelMetrics.mape}%</div>
              <p className="text-xs text-muted-foreground">Excellent accuracy</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">RMSE</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">
                        Root Mean Square Error - measures the standard deviation of prediction errors. Lower values indicate better fit.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-2xl font-bold">{modelMetrics.rmse}</div>
              <p className="text-xs text-muted-foreground">Low error variance</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">MAE</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">
                        Mean Absolute Error - average of absolute differences between predictions and actual values. Lower is better.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-2xl font-bold">{modelMetrics.mae}</div>
              <p className="text-xs text-muted-foreground">Minimal deviation</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">R² Score</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">
                        Coefficient of Determination - indicates how well the model explains variance in the data. Closer to 1.0 is better.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-2xl font-bold">{modelMetrics.r2Score}</div>
              <p className="text-xs text-muted-foreground">Strong correlation</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={toggleHelp}
        content={helpContent}
      />
    </div>
  );
}
